import type { BoxProps } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import {
  Tab,
  Tabs,
} from 'components/ui/common/Tabs/index.js';
import { SingleColumnForm } from 'components/ui/controlled-form/SingleColumnForm/index.js';
import { TitleField } from 'components/ui/form/FormLabel/TitleField.js';
import { merge } from 'es-toolkit';
import type {
  Control,
  DeepMap,
  DeepPartial,
  FieldError,
  FieldValues,
  UseFormClearErrors,
  UseFormResetField,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';

import type {
  FormSectionItem,
  GenericFormProps,
} from '../GenericForm/GenericForm.types.js';
import { useFormSectionEffects } from './FormSection.effects.js';
import {
  FormGridContainer,
  Label,
  StyledAsterisk,
  StyledDivider,
  StyledStepper,
  SubTitle,
  Title,
} from './FormSection.styled.js';

interface FormSectionProps<
  FormValues extends FieldValues = Record<string, any>,
> {
  fields: FormSectionItem['fields'];
  control: Control<FormValues>;
  errors: DeepMap<DeepPartial<FormValues>, FieldError>;
  setError: UseFormSetError<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  resetField: UseFormResetField<FormValues>;
  clearErrors: UseFormClearErrors<FormValues>;
  showSubtitle?: boolean;
  showDivider?: boolean;
  title?: string;
  wrapperProps?: BoxProps;
  stepperProps?: GenericFormProps['stepperProps'];
}

export const FormSection = ({
  fields,
  showDivider,
  showSubtitle,
  title,
  stepperProps,
  wrapperProps,
  ...props
}: FormSectionProps) => {
  const { currentTab, onChangeTabHandler } = useFormSectionEffects();

  return (
    <Box mt={4} position="relative" width="100%" {...wrapperProps}>
      {title && (
        <>
          <Title data-sentry-unmask>{title}</Title>
          {showSubtitle && (
            <SubTitle data-sentry-unmask>
              <StyledAsterisk>*</StyledAsterisk>
              {' '}
              indicates required fields.
            </SubTitle>
          )}
        </>
      )}
      <Box mt={7}>
        {stepperProps?.steps && (
          <Box mb={3.75}>
            <StyledStepper activeStep={stepperProps.activeStep}>
              {stepperProps.steps.map(label => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </StyledStepper>
          </Box>
        )}
        {fields.map((field, index) => {
          return (
            <Box
              mb={5.5}
              key={`form-section-${field.label}-${index}`}
              {...field?.wrapperProps}
            >
              <FormGridContainer container>
                {field?.tabs
                  ? (
                      <Box display="flex" flexDirection="column" width="100%">
                        <Tabs
                          value={currentTab}
                          onChange={onChangeTabHandler}
                        >
                          {field.tabs?.map((tab) => {
                            return (
                              <Tab
                                key={`tab-${tab.label}`}
                                label={tab.label}
                              />
                            );
                          })}
                        </Tabs>
                        <Grid {...merge({ size: { xs: 12, md: 5 } }, field.labelWrapperProps ?? {})}>
                          <Label style={{ marginTop: 20 }}>
                            {field.inputs[currentTab]?.config?.label ?? ''}
                          </Label>
                        </Grid>
                        <Grid {...merge({ size: { xs: 12, md: 8 } }, field.inputsWrapperProps ?? {})}>
                          <SingleColumnForm
                            fields={[field.inputs[currentTab]]}
                            {...props}
                          />
                        </Grid>
                      </Box>
                    )
                  : (
                      <>
                        <Grid {...merge({ size: { xs: 12, md: 4 } }, field.labelWrapperProps ?? {})}>
                          <TitleField
                            title={field.label}
                            required={field.required}
                            description={field?.description}
                          />
                        </Grid>
                        <Grid {...merge({ size: { xs: 12, md: 8 } }, field.inputsWrapperProps ?? {})}>
                          <SingleColumnForm fields={field.inputs} {...props} />
                        </Grid>
                      </>
                    )}
              </FormGridContainer>
            </Box>
          );
        })}
      </Box>
      {showDivider && <StyledDivider />}
    </Box>
  );
};
